
import React, { useState, useEffect } from 'react';
import { getCrystalBallPrediction } from '../services/geminiService';
import { getPreloadedCrystalBallPrediction } from '../data/crystalBallPreloaded';
import { PhoneIcon, StarIcon } from './Icons';
import { useClient } from '../contexts/ClientContext';
import { DivinationBlurLock } from './DivinationBlurLock';

const CrystalBall: React.FC = () => {
    const { client, recordDivinationActivity } = useClient();
    const [question, setQuestion] = useState('');
    const [isGazing, setIsGazing] = useState(false);
    const [prediction, setPrediction] = useState<string | null>(null);
    const [particles, setParticles] = useState<{id: number, left: number, top: number, delay: number, size: number}[]>([]);
    
    useEffect(() => {
        const newParticles = Array.from({ length: 30 }).map((_, i) => ({
            id: i,
            left: Math.random() * 100,
            top: Math.random() * 100,
            delay: Math.random() * 5,
            size: Math.random() * 3 + 1
        }));
        setParticles(newParticles);
    }, []);

    const handleGaze = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!question.trim()) return;
        
        setIsGazing(true);
        setPrediction(null);
        
        // Lancement de la requête API
        const predictionPromise = getCrystalBallPrediction(question);
        
        // On attend au moins 4 secondes pour l'animation, même si l'API répond vite
        setTimeout(async () => {
            try {
                const predictionText = await predictionPromise;
                let finalPred: string;
                if (predictionText && predictionText.trim().length > 0) {
                    finalPred = predictionText;
                } else {
                    finalPred = getPreloadedCrystalBallPrediction(question);
                }
                setPrediction(finalPred);

                if (client && finalPred) {
                    recordDivinationActivity('crystal', 'Boule de Cristal', `Question : "${question}"`, { question, prediction: finalPred });
                }
            } catch (err) {
                console.warn("Erreur prédiction boule de cristal, utilisation de la base préchargée:", err);
                const fallback = getPreloadedCrystalBallPrediction(question);
                setPrediction(fallback);
                if (client && fallback) {
                    recordDivinationActivity('crystal', 'Boule de Cristal', `Question : "${question}"`, { question, prediction: fallback });
                }
            } finally {
                setIsGazing(false);
            }
        }, 4000); 
    };

    const reset = () => {
        setPrediction(null);
        setQuestion('');
    };

    return (
        <section className="py-12 bg-transparent min-h-screen flex flex-col items-center justify-center relative overflow-hidden select-none">
            <style>{`
                @keyframes nebulaspin {
                    0% { transform: rotate(0deg) scale(1); }
                    50% { transform: rotate(180deg) scale(1.2); }
                    100% { transform: rotate(360deg) scale(1); }
                }
                @keyframes float-particle {
                    0%, 100% { transform: translateY(0) opacity(0); }
                    50% { opacity: 0.8; }
                    100% { transform: translateY(-120px) opacity(0); }
                }
                @keyframes energy-pulse {
                    0% { box-shadow: 0 0 20px rgba(168,85,247,0.2), 0 0 40px rgba(168,85,247,0.1); }
                    50% { box-shadow: 0 0 80px rgba(168,85,247,0.8), 0 0 150px rgba(234,179,8,0.5); }
                    100% { box-shadow: 0 0 20px rgba(168,85,247,0.2), 0 0 40px rgba(168,85,247,0.1); }
                }
                @keyframes lightning {
                    0%, 85% { opacity: 0; }
                    86% { opacity: 0.8; background-color: rgba(255,255,255,0.8); }
                    88% { opacity: 0; }
                    93% { opacity: 0.6; background-color: rgba(255,255,255,0.6); }
                    95% { opacity: 0; }
                    100% { opacity: 0; }
                }
                @keyframes orbit-rune {
                    from { transform: rotate(0deg) translateX(140px) rotate(0deg); }
                    to { transform: rotate(360deg) translateX(140px) rotate(-360deg); }
                }
                @keyframes star-rotate {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes twinkle {
                    0%, 100% { opacity: 0.3; transform: scale(0.8); }
                    50% { opacity: 1; transform: scale(1.4); box-shadow: 0 0 4px white; }
                }
                .glass-surface {
                    background: radial-gradient(circle at 30% 30%, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.02) 20%, transparent 60%);
                    box-shadow: inset -10px -20px 50px rgba(0,0,0,0.9), inset 5px 5px 20px rgba(255,255,255,0.3);
                }
            `}</style>

            {/* Container */}
            <div className="container mx-auto px-4 relative z-10 max-w-2xl text-center flex flex-col items-center">
                
                <div className={`transition-all duration-700 ${prediction ? 'opacity-0 translate-y-[-20px] h-0 overflow-hidden' : 'opacity-100'}`}>
                    <h2 className="text-4xl md:text-5xl font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-gold-200 to-purple-300 mb-4 drop-shadow-[0_0_15px_rgba(168,85,247,0.5)]">
                        L'Oracle de Cristal
                    </h2>
                    <p className="text-purple-200/80 mb-8 italic text-lg font-light">
                        Concentrez-vous sur votre question... Les esprits écoutent.
                    </p>
                </div>

                {/* === THE CRYSTAL BALL === */}
                <div className="relative w-72 h-72 md:w-96 md:h-96 mb-12 group">
                    
                    {/* Magical Aura / Glow behind */}
                    <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] rounded-full bg-purple-600/20 blur-[80px] transition-all duration-1000 ${isGazing ? 'opacity-100 scale-125 bg-indigo-500/50 mix-blend-screen' : 'opacity-40 scale-100'}`}></div>

                    {isGazing && (
                        <div className="absolute inset-0 z-0">
                             {[...Array(6)].map((_, i) => (
                                <div 
                                    key={i}
                                    className="absolute top-[45%] left-[45%] text-gold-400/60 font-serif text-xl animate-spin"
                                    style={{ 
                                        animation: `orbit-rune ${6 + i}s linear infinite`,
                                        animationDelay: `${i * -1}s` 
                                    }}
                                >
                                    {['◆', '◇', '●', '○', '■', '□'][i]}
                                </div>
                             ))}
                        </div>
                    )}

                    <div className={`w-full h-full rounded-full relative z-20 transition-transform duration-[3s] ${isGazing ? 'scale-[1.05]' : 'hover:scale-[1.01]'}`}
                         style={{ animation: isGazing ? 'energy-pulse 0.5s infinite alternate' : 'none' }}>
                        
                        <div className="absolute inset-0 rounded-full bg-black opacity-90"></div>

                        <div className="absolute inset-0 rounded-full overflow-hidden mask-image-radial transform transition-all">
                            <div className={`absolute inset-[-50%] w-[200%] h-[200%] bg-[radial-gradient(circle,_rgba(76,29,149,0.8)_0%,_transparent_60%)] filter blur-xl transition-opacity duration-1000 ${isGazing ? 'opacity-90' : 'opacity-40'}`}
                                 style={{ animation: `nebulaspin ${isGazing ? '1.5s' : '25s'} linear infinite` }}></div>
                            
                            <div className={`absolute inset-[-50%] w-[200%] h-[200%] bg-[radial-gradient(circle,_rgba(251,191,36,0.6)_0%,_transparent_50%)] filter blur-xl mix-blend-screen transition-opacity duration-500 ${isGazing ? 'opacity-80' : 'opacity-0'}`}
                                 style={{ animation: `nebulaspin ${isGazing ? '1s' : '15s'} linear infinite reverse` }}></div>
                            
                            <div className={`absolute inset-[-20%] w-[140%] h-[140%] bg-[conic-gradient(from_0deg,_transparent_0deg,_rgba(59,130,246,0.4)_180deg,_transparent_360deg)] filter blur-2xl transition-opacity duration-1000 ${isGazing ? 'opacity-100' : 'opacity-20'}`}
                                 style={{ animation: 'spin 8s linear infinite' }}></div>

                            <div className="absolute inset-[-50%] w-[200%] h-[200%] animate-[star-rotate_60s_linear_infinite] opacity-80 pointer-events-none">
                                {[...Array(60)].map((_, i) => (
                                    <div 
                                        key={`star-${i}`}
                                        className="absolute bg-white rounded-full mix-blend-overlay"
                                        style={{
                                            top: `${Math.random() * 100}%`,
                                            left: `${Math.random() * 100}%`,
                                            width: Math.random() < 0.1 ? '3px' : Math.random() < 0.3 ? '2px' : '1px',
                                            height: Math.random() < 0.1 ? '3px' : Math.random() < 0.3 ? '2px' : '1px',
                                            opacity: Math.random() * 0.9 + 0.1,
                                            boxShadow: Math.random() > 0.8 ? '0 0 2px white' : 'none',
                                            animation: `twinkle ${Math.random() * 3 + 1.5}s infinite ease-in-out ${Math.random() * 2}s`
                                        }}
                                    ></div>
                                ))}
                            </div>
                            
                             <div className="absolute inset-0 rounded-full mix-blend-overlay" style={{ animation: isGazing ? 'lightning 1.5s infinite' : 'none' }}></div>
                        </div>

                        <div className="absolute inset-0 rounded-full glass-surface border border-white/10 z-30"></div>

                        <div className="absolute top-[15%] left-[20%] w-[25%] h-[15%] bg-gradient-to-b from-white to-transparent opacity-50 rounded-[100%] blur-[3px] transform -rotate-45 z-40"></div>
                        <div className="absolute bottom-[15%] right-[20%] w-[10%] h-[5%] bg-purple-300 opacity-30 rounded-[100%] blur-[4px] z-40"></div>

                        {isGazing && (
                            <div className="absolute inset-0 rounded-full z-30 opacity-80 mix-blend-color-dodge animate-pulse">
                                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/shattered-island.png')] opacity-60 bg-fixed filter contrast-200"></div>
                            </div>
                        )}
                        
                        {particles.map((p) => (
                            <div 
                                key={p.id}
                                className="absolute bg-white rounded-full z-50 animate-pulse"
                                style={{
                                    left: `${p.left}%`,
                                    top: `${p.top}%`,
                                    width: `${p.size}px`,
                                    height: `${p.size}px`,
                                    opacity: Math.random() * 0.8,
                                    boxShadow: `0 0 ${p.size * 2}px white`,
                                    animation: `float-particle ${2 + p.delay}s infinite ease-in-out`
                                }}
                            />
                        ))}
                    </div>

                    <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-48 h-16 z-10 flex flex-col items-center">
                        <div className="w-32 h-4 bg-gradient-to-r from-yellow-700 via-yellow-300 to-yellow-700 rounded-[50%] shadow-lg translate-y-2 z-20"></div>
                        <div className="w-24 h-8 bg-gradient-to-b from-gray-900 via-gray-800 to-black z-10 -translate-y-1 border-x border-gray-700"></div>
                        <div className="w-40 h-6 bg-gradient-to-r from-yellow-800 via-yellow-400 to-yellow-800 rounded-[50%] -translate-y-4 shadow-[0_10px_20px_rgba(0,0,0,0.8)] z-0"></div>
                    </div>
                </div>

                {!prediction ? (
                    <form 
                        onSubmit={handleGaze} 
                        className={`w-full max-w-lg transition-all duration-700 transform ${isGazing ? 'opacity-0 scale-90 translate-y-10 pointer-events-none' : 'opacity-100 scale-100'}`}
                    >
                        <div className="relative group">
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full blur opacity-25 group-hover:opacity-50 transition duration-500"></div>
                            <input 
                                type="text" 
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                placeholder="Quelle est votre question ?"
                                className="relative w-full bg-black/80 border border-purple-500/30 rounded-full px-8 py-5 text-white text-center text-lg placeholder-gray-500 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-all shadow-inner"
                            />
                        </div>
                        
                        <div className="mt-8 flex justify-center">
                            <button 
                                type="submit"
                                disabled={!question.trim()}
                                className="relative px-12 py-4 bg-transparent overflow-hidden rounded-full group disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-purple-800 to-indigo-800 opacity-80 group-hover:opacity-100 transition-opacity"></span>
                                <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent"></span>
                                <span className="relative text-white font-serif font-bold tracking-widest uppercase flex items-center gap-2">
                                    <StarIcon className="w-4 h-4 text-gold-400" /> Révéler <StarIcon className="w-4 h-4 text-gold-400" />
                                </span>
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className="animate-fade-in-up w-full max-w-xl relative z-50 -mt-10">
                        {/* SI NON CONNECTÉ -> CADENAS & OVERLAY FLOUTÉ */}
                        {!client && (
                            <DivinationBlurLock 
                                title="Vision de la Boule de Cristal Prête"
                                subtitle="Le flash médiumnique et la réponse des astres à votre question ont traversé le cristal. Connectez-vous ou créez votre compte gratuit en 10 secondes pour dévoiler votre prédiction complète."
                            />
                        )}

                        <div className={`bg-mystic-900/90 border-2 border-gold-500/30 p-8 rounded-2xl backdrop-blur-xl shadow-[0_0_50px_rgba(168,85,247,0.3)] relative text-center overflow-hidden ${!client ? 'filter blur-sm select-none pointer-events-none opacity-40' : ''}`}>
                            <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-gold-400"></div>
                            <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-gold-400"></div>
                            <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-gold-400"></div>
                            <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-gold-400"></div>

                            <h3 className="text-gold-400 font-serif text-sm uppercase tracking-[0.3em] mb-6">La réponse des astres</h3>
                            <p className="text-white text-2xl md:text-3xl font-medium leading-relaxed mb-8 drop-shadow-md font-serif">"{prediction}"</p>

                            <div className="flex flex-col gap-4">
                                <button onClick={reset} className="text-gray-400 hover:text-white text-sm underline decoration-gray-600 underline-offset-4 transition-colors">Poser une autre question</button>
                                <a href="tel:0985405274" className="mt-2 flex items-center justify-center gap-3 bg-gradient-to-r from-green-800 to-green-600 hover:from-green-700 hover:to-green-500 text-white py-3 px-6 rounded-lg font-bold shadow-lg transition-transform hover:scale-[1.02]">
                                    <PhoneIcon className="w-5 h-5" />
                                    <span>Approfondir au 09 85 40 52 74</span>
                                </a>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default CrystalBall;
